import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { splitListItems } from "@/lib/utils";
import { Loading, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Pencil, FileText, Upload, Download, ShieldAlert, Ban, RotateCcw, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminEmployeeHR from "@/components/AdminEmployeeHR";
import AdminEmployeeLeave from "@/components/AdminEmployeeLeave";
import AdminEmployeeSalary from "@/components/AdminEmployeeSalary";

// 0=Monday..6=Sunday (Python date.weekday() convention, matches the backend).
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// A few HR-entered fields (Additional Charge in particular) are plain
// single-value text inputs, so a multi-item list pasted in one go can land
// as one long blob instead of several records. splitListItems detects that
// and lets Row render it as a proper bullet list — ordinary short values are
// returned unchanged, so this is safe to apply to every Row.
function Row({ label, value }) {
  const items = splitListItems(value);
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-slate-50 last:border-0">
      <span className="overline text-slate-400">{label}</span>
      {items.length > 1 ? (
        <ul className="space-y-0.5 mt-0.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm text-navy flex items-start gap-1.5">
              <span className="h-1 w-1 rounded-full bg-navy/40 mt-1.5 shrink-0" />{item}
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-sm text-navy">{value || "—"}</span>
      )}
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const fileRef = useRef();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [docType, setDocType] = useState("");
  const [docDate, setDocDate] = useState("");
  const [expiry, setExpiry] = useState("");
  const [remarks, setRemarks] = useState("");

  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("Left");
  const [reason, setReason] = useState("");
  const [leaveDate, setLeaveDate] = useState("");

  const { data: emp, isLoading } = useQuery({ queryKey: ["employee", id], queryFn: async () => (await api.get(`/employees/${id}`)).data });
  const { data: docTypes } = useQuery({ queryKey: ["docTypes"], queryFn: async () => (await api.get("/document-types")).data });

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", fileRef.current.files[0]);
      fd.append("employee_id", id);
      fd.append("document_type", docType);
      fd.append("document_date", docDate);
      fd.append("expiry_date", expiry);
      fd.append("remarks", remarks);
      return (await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Document uploaded"); qc.invalidateQueries({ queryKey: ["employee", id] }); setUploadOpen(false); setDocType(""); setExpiry(""); setDocDate(""); setRemarks(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  // Employees can never re-upload or replace a submitted document themselves
  // (see MyDocuments.js) — this is the only way to let them try again: HR
  // archives the current file, which flips that document type back to
  // "Pending" on the employee's own checklist.
  const resetDoc = useMutation({
    mutationFn: async (docId) => (await api.delete(`/documents/${docId}`)).data,
    onSuccess: () => { toast.success("Document reset — the employee can submit a new one"); qc.invalidateQueries({ queryKey: ["employee", id] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const confirmReset = (d) => {
    if (window.confirm(`Reset "${d.document_type}" back to Pending? The employee will be able to upload a new document for it. This file will stay in the audit trail but will no longer be the active one.`)) {
      resetDoc.mutate(d.id);
    }
  };

  // Employee self-submissions land here first (local disk only — never
  // Google Drive) and need this explicit HR/Super Admin verification before
  // they're filed. Approve pushes the file to Drive; reject discards it and
  // lets the employee try again (they're notified in-app either way).
  const verifyDoc = useMutation({
    mutationFn: async ({ docId, action, remarks }) => (await api.post(`/documents/${docId}/verify`, { action, remarks })).data,
    onSuccess: (res) => { toast.success(res.status === "Active" ? "Document verified and filed" : "Document rejected — employee notified"); qc.invalidateQueries({ queryKey: ["employee", id] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const approveDoc = (d) => {
    if (window.confirm(`Verify and file "${d.document_type}"? This uploads it to Google Drive and marks it Active.`)) {
      verifyDoc.mutate({ docId: d.id, action: "approve" });
    }
  };

  const rejectDoc = (d) => {
    const remarks = window.prompt(`Reject "${d.document_type}"? Optionally add a note for the employee (shown in their notification), or leave blank.`, "");
    if (remarks !== null) {
      verifyDoc.mutate({ docId: d.id, action: "reject", remarks: remarks || "" });
    }
  };

  const changeStatus = useMutation({
    mutationFn: async () => (await api.patch(`/employees/${id}/status`, { status: newStatus, reason, date_of_leaving: leaveDate, reason_for_leaving: reason })).data,
    onSuccess: (res) => {
      toast.success(res?.login_locked ? "Status updated — portal login locked" : "Status updated");
      qc.invalidateQueries({ queryKey: ["employee", id] });
      setStatusOpen(false);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const openStatusDialog = () => {
    const em = emp?.employment || {};
    // Prefill with whatever is already on record so this doubles as an "edit"
    // when the employee is already marked Left/Terminated/Retired — HR can
    // fix a missing/wrong exit date instead of being stuck with the original
    // one-time entry.
    setNewStatus(em.employment_status && em.employment_status !== "Active" ? em.employment_status : "Left");
    setLeaveDate(em.date_of_leaving || "");
    setReason(em.reason_for_leaving || "");
    setStatusOpen(true);
  };

  const download = async (docId, name) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    } catch { toast.error("Unable to download"); }
  };

  if (isLoading || !emp) return <Loading />;
  const em = emp.employment || {}, st = emp.statutory || {}, idn = emp.identity || {};
  const additionalChargeItems = splitListItems(em.additional_charge);

  return (
    <div>
      <button onClick={() => navigate("/employees")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy mb-4" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> All Employees</button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-navy/5 grid place-items-center text-navy font-serif-display text-2xl">{idn.name?.[0]?.toUpperCase()}</div>
          <div>
            <p className="overline text-teal">{emp.employee_id}</p>
            <h1 className="font-serif-display text-3xl tracking-tight text-navy">{idn.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-slate-500 flex-wrap">
              <span>{emp.designation_title || "—"}</span><span>·</span><span>{emp.department_name || "—"}</span>
              <StatusBadge status={em.employment_status} />
              {additionalChargeItems.length > 0 && (
                <span className="text-[10px] bg-teal/10 text-teal rounded-full px-2 py-0.5">
                  + {additionalChargeItems.length > 1 ? `${additionalChargeItems.length} additional charges` : additionalChargeItems[0]}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/employees/${id}/edit`)} data-testid="edit-employee"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
          {isSuperAdmin && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openStatusDialog()} data-testid="change-status">
              <Ban className="h-4 w-4 mr-1" /> {em.employment_status && em.employment_status !== "Active" ? "Update Exit Details" : "Change Status"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="hr" data-testid="tab-hr">HR Management</TabsTrigger>
          <TabsTrigger value="salary" data-testid="tab-salary">Salary</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({emp.documents?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="grid lg:grid-cols-3 gap-4 mt-6">
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-serif-display text-xl text-navy mb-3">Identity</h3>
            <Row label="Father's Name" value={idn.father_name} />
            <Row label="Mother's Name" value={idn.mother_name} />
            <Row label="Date of Birth" value={idn.dob} />
            <Row label="Gender" value={idn.gender} />
            <Row label="Blood Group" value={idn.blood_group} />
            <Row label="Mobile" value={idn.mobile} />
            <Row label="Personal Email" value={idn.personal_email} />
            <Row label="Emergency Contact" value={idn.emergency_contact_name && `${idn.emergency_contact_name} · ${idn.emergency_contact_number}`} />
          </Card>
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-serif-display text-xl text-navy mb-3">Employment</h3>
            <Row label="Employee Type" value={em.employee_type} />
            <Row label="Weekly Off Day" value={em.weekly_off_day == null ? "Not yet assigned (defaults to Sunday)" : WEEKDAY_NAMES[em.weekly_off_day]} />
            <Row label="Field / Remote Staff" value={em.is_field_staff ? "Yes — no fixed location, shift or timing required" : "No"} />
            <Row label="Backup Required on Leave" value={em.backup_required ? "Yes — must name a colleague to cover when applying for leave" : "No"} />
            {em.honorary_no_salary && <Row label="Payroll" value="Honorary — no salary; not included in the payroll run" />}
            <Row label="Position" value={em.position} />
            <Row label="Reporting Officer" value={emp.reporting_manager_name} />
            <Row label="Additional Charge" value={em.additional_charge} />
            <Row label="Project / Site" value={em.project_site} />
            <Row label="Date of Joining" value={em.doj} />
            <Row label="Confirmation Date" value={em.confirmation_date} />
            <Row label="Probation End" value={em.probation_end_date} />
            {!em.confirmation_date && emp.probation_status && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 mt-1">
                <p className="overline text-amber-600">Estimated Probation End (auto-calculated)</p>
                <p className="text-sm font-semibold text-navy mt-0.5">
                  {emp.probation_status.adjusted_probation_end}
                  {emp.probation_status.extension_days > 0 && (
                    <span className="text-xs text-amber-600 font-normal ml-2">
                      Base 6-month end was {emp.probation_status.base_probation_end} — extended {emp.probation_status.extension_days} day(s)
                      for {emp.probation_status.cl_taken_during_probation} CL taken (beyond the 6 allowed during probation).
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">Informational only — set Confirmation Date above once you're ready to confirm.</p>
              </div>
            )}
            {em.employee_type === "Contract" && (
              <>
                <Row label="Contract Start Date" value={em.contract_start_date} />
                <Row label="Contract End Date" value={em.contract_end_date} />
                <Row label="Contract Extended Until" value={em.contract_extended_date || "NA"} />
              </>
            )}
            {em.employee_type === "Consultant" ? (
              <Row label="Monthly Fee" value={emp?.consultant_monthly_fee ? `₹ ${emp.consultant_monthly_fee.toLocaleString("en-IN")}` : "Not set"} />
            ) : (
              <>
                <Row label="Salary Grade" value={em.salary_grade} />
                <Row label="CTC" value={em.ctc ? `₹ ${em.ctc.toLocaleString("en-IN")}` : ""} />
              </>
            )}
          </Card>
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-serif-display text-xl text-navy mb-3 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-teal" /> Statutory</h3>
            <Row label="PAN" value={st.pan} />
            <Row label="Aadhaar Ref" value={st.aadhaar_ref && `••••${st.aadhaar_ref}`} />
            <Row label="UAN" value={st.uan} />
            <Row label="Bank / IFSC" value={st.bank_account && `${st.bank_account} · ${st.ifsc}`} />
            <Row label="PF Applicable" value={st.pf_applicable ? "Yes" : "No"} />
            <Row label="ESIC Applicable" value={st.esic_applicable ? `Yes (${st.esic_number || "—"})` : "No"} />
            <Row label="Professional Tax" value={st.professional_tax_applicable ? "Yes" : "No"} />
          </Card>
        </TabsContent>

        <TabsContent value="hr">
          <AdminEmployeeHR employee={emp} onChange={() => qc.invalidateQueries({ queryKey: ["employee", id] })} />
        </TabsContent>

        <TabsContent value="salary" className="mt-6">
          <AdminEmployeeSalary employee={emp} onChange={() => qc.invalidateQueries({ queryKey: ["employee", id] })} />
        </TabsContent>

        <TabsContent value="leave" className="mt-6">
          <AdminEmployeeLeave employee={emp} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <div className="flex justify-between items-center mb-4 gap-3">
            <p className="text-xs text-slate-400">
              Documents the employee submitted themselves show "Pending Verification" first — they're held on this
              server only, not yet in Google Drive. Preview the file, then Verify (files it to Drive) or Reject
              (employee is notified and can re-submit).
            </p>
            <Button className="bg-navy hover:bg-navy-700 btn-lift shrink-0" onClick={() => setUploadOpen(true)} data-testid="upload-doc-btn"><Upload className="h-4 w-4 mr-1" /> Upload Document</Button>
          </div>
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Type</TableHead>
                <TableHead className="overline text-slate-500">File</TableHead>
                <TableHead className="overline text-slate-500">Ver</TableHead>
                <TableHead className="overline text-slate-500">Expiry</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="text-right overline text-slate-500">File</TableHead>
                <TableHead className="text-right overline text-slate-500">Verify / Reset</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(emp.documents || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No documents uploaded yet.</TableCell></TableRow>
                ) : emp.documents.map((d) => (
                  <TableRow key={d.id} className="table-row-i" data-testid={`doc-row-${d.id}`}>
                    <TableCell className="font-medium text-navy flex items-center gap-2"><FileText className="h-4 w-4 text-teal" /> {d.document_type}</TableCell>
                    <TableCell className="text-slate-600 max-w-[180px] truncate">{d.file_name}</TableCell>
                    <TableCell className="text-slate-500">v{d.version}</TableCell>
                    <TableCell className="text-slate-600">{d.expiry_date || "—"}</TableCell>
                    <TableCell><StatusBadge status={d.expiry_status === "None" ? d.status : d.expiry_status} /></TableCell>
                    <TableCell className="text-right">
                      {d.status !== "Rejected" && (
                        <Button size="sm" variant="ghost"
                          title={d.status === "PendingVerification" ? "Preview before verifying" : undefined}
                          onClick={() => download(d.id, d.file_name)} data-testid={`download-${d.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.status === "PendingVerification" ? (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="text-emerald-700" title="Verify & file to Google Drive"
                            onClick={() => approveDoc(d)} data-testid={`approve-${d.id}`}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" title="Reject — employee will be asked to submit again"
                            onClick={() => rejectDoc(d)} data-testid={`reject-${d.id}`}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : d.status !== "Archived" && d.status !== "Rejected" && (
                        <Button size="sm" variant="ghost" title="Reset to Pending — lets the employee submit a new document"
                          onClick={() => confirmReset(d)} data-testid={`reset-${d.id}`}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-white" data-testid="upload-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Document Type *</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1" data-testid="doc-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-white">{(docTypes || []).map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Document Date</Label><Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} className="mt-1" /></div>
              <div><Label>Expiry Date</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" data-testid="doc-expiry" /></div>
            </div>
            <div><Label>File * (PDF / image / Word, max {(docTypes || []).find((t) => t.name === docType)?.max_size_mb || 15} MB)</Label><Input type="file" ref={fileRef} className="mt-1" data-testid="doc-file" /></div>
            <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!docType || upload.isPending} onClick={() => upload.mutate()} data-testid="submit-upload">Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status dialog (super admin) */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="bg-white" data-testid="status-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Change Employment Status</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Requires Super Admin approval. Recorded in the audit trail; records are archived, never deleted.</p>
          {em.employment_status && em.employment_status !== "Active" && (
            <p className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-md px-3 py-2 -mt-1">
              This employee is currently marked <strong>{em.employment_status}</strong>. You can correct their exit date/reason below,
              or set status back to <strong>Active</strong> to reinstate them and re-enable their portal login.
            </p>
          )}
          <div className="space-y-4">
            <div><Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["Active", "Notice", "Left", "Terminated", "Retired"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date of Leaving</Label><Input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)} className="mt-1" data-testid="status-leave-date" /></div>
            <div><Label>Reason *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="status-reason" /></div>
            {newStatus === "Active" && (
              <p className="text-xs text-slate-500">Setting status to Active re-enables this employee's portal login (if they have one) and un-archives their record.</p>
            )}
            {["Left", "Terminated", "Retired"].includes(newStatus) && (
              <p className="text-xs text-slate-500">Their portal login (if any) will be locked immediately on saving.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={reason.length < 3 || changeStatus.isPending} onClick={() => changeStatus.mutate()} data-testid="submit-status">Confirm Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
